import { useState, useEffect, useRef, useCallback } from 'react'
import { Autocomplete, TextField, CircularProgress } from '@mui/material'
import api from '@services/api'

/**
 * A searchable single-select dropdown backed by a paginated DRF endpoint.
 *
 * Instead of loading only the first page of results (the previous behaviour of
 * the plain <Select> dropdowns), this debounces user input and queries the API
 * with `?search=`, so the full dataset is reachable regardless of size.
 *
 * Props:
 *  - endpoint:     API path to query, e.g. '/assets/locations/'
 *  - value:        currently selected id (string | number | '')
 *  - onChange:     (id) => void  -- receives the selected id ('' when cleared)
 *  - getOptionLabel: (option) => string  -- how to render each option
 *  - label:        field label
 *  - pageSize:     number of results to request per search (default 20)
 *  - extraParams:  optional object of additional query params
 *  - disabled, required, error, helperText: passed through to the input
 */
const AsyncSearchSelect = ({
  endpoint,
  value,
  onChange,
  getOptionLabel,
  label,
  pageSize = 20,
  extraParams = {},
  disabled = false,
  required = false,
  error = false,
  helperText = '',
}) => {
  const [open, setOpen] = useState(false)
  const [options, setOptions] = useState([])
  const [selectedOption, setSelectedOption] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [loading, setLoading] = useState(false)
  const debounceRef = useRef(null)
  // Stringify so the effect doesn't re-run on every render due to a new object ref.
  const extraParamsKey = JSON.stringify(extraParams)

  const fetchOptions = useCallback(async (search) => {
    setLoading(true)
    try {
      const res = await api.get(endpoint, {
        params: { search, page_size: pageSize, ...extraParams },
      })
      setOptions(res.data.results || res.data || [])
    } catch {
      setOptions([])
    } finally {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, pageSize, extraParamsKey])

  // Resolve the full object for the currently selected id (needed when editing
  // an existing record where the option isn't in the current search results).
  useEffect(() => {
    let cancelled = false
    if (value === '' || value === null || value === undefined) {
      setSelectedOption(null)
      return
    }
    // Already selected the same id? Nothing to do.
    if (selectedOption && selectedOption.id === value) return
    const existing = options.find((o) => o.id === value)
    if (existing) {
      setSelectedOption(existing)
      return
    }
    api.get(`${endpoint}${value}/`)
      .then((res) => { if (!cancelled) setSelectedOption(res.data) })
      .catch(() => { if (!cancelled) setSelectedOption(null) })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, endpoint])

  // Debounced search whenever the dropdown is open and the search text changes.
  useEffect(() => {
    if (!open) return
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => fetchOptions(searchTerm), 300)
    return () => debounceRef.current && clearTimeout(debounceRef.current)
  }, [searchTerm, open, fetchOptions])

  return (
    <Autocomplete
      open={open}
      onOpen={() => { setOpen(true); fetchOptions(searchTerm) }}
      onClose={() => { setOpen(false); setSearchTerm('') }}
      value={selectedOption}
      onChange={(_, newValue) => {
        setSelectedOption(newValue)
        onChange(newValue ? newValue.id : '')
      }}
      onInputChange={(_, newInput, reason) => {
        // Only the user's typing should drive the search query. Selecting an
        // option ('reset') must not overwrite the search with the label.
        if (reason === 'input') setSearchTerm(newInput)
      }}
      options={options}
      getOptionLabel={(option) => (option ? getOptionLabel(option) : '')}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      loading={loading}
      disabled={disabled}
      filterOptions={(x) => x}
      fullWidth
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          error={error}
          placeholder="Type to search..."
          helperText={helperText || 'Type to search'}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  )
}

export default AsyncSearchSelect
